// Bakes EXIF Orientation into raw pixels for JPEG uploads so the stored image
// looks upright regardless of how the downstream viewer (CDN, in-app webview,
// canvas-based PDF export) handles the EXIF tag.
//
// Strategy:
//   1. Cheap path: read only the first 128 KB and parse the APP1/Exif segment
//      to find tag 0x0112 (Orientation). If the value is 1 or absent, return
//      the original File untouched — no decode, no re-encode, no quality loss.
//   2. Hot path (orientation 2–8): decode via createImageBitmap with
//      imageOrientation: "from-image" so the browser applies the rotation,
//      then re-encode the canvas as JPEG. The output has no EXIF, so every
//      downstream consumer sees upright pixels.
//
// Any failure (parsing, decoding, encoding) falls back to the original File —
// upload is never blocked by orientation normalization.

const JPEG_MIME = new Set(["image/jpeg", "image/jpg", "image/pjpeg"]);

// Reads EXIF Orientation (tag 0x0112) from the start of a JPEG. Returns 1 for
// "no rotation needed" in every malformed / unsupported case, so callers can
// safely treat 1 as "nothing to do".
function readJpegOrientation(buffer) {
  if (!buffer || buffer.byteLength < 4) return 1;
  const view = new DataView(buffer);
  if (view.getUint16(0) !== 0xffd8) return 1; // not JPEG (no SOI)

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    offset += 2;
    if ((marker & 0xff00) !== 0xff00) return 1; // malformed
    if (marker === 0xffda) return 1; // SOS — image data starts, no EXIF found
    const segSize = view.getUint16(offset);
    if (segSize < 2 || offset + segSize > view.byteLength) return 1;

    if (marker === 0xffe1 && offset + 8 <= view.byteLength) {
      // APP1 — check for "Exif\0\0" identifier
      const isExif =
        view.getUint32(offset + 2) === 0x45786966 && // "Exif"
        view.getUint16(offset + 6) === 0x0000;
      if (isExif) {
        const tiff = offset + 8;
        if (tiff + 8 > view.byteLength) return 1;
        const bom = view.getUint16(tiff);
        const little = bom === 0x4949;
        if (!little && bom !== 0x4d4d) return 1;
        if (view.getUint16(tiff + 2, little) !== 0x002a) return 1;
        const ifd0 = tiff + view.getUint32(tiff + 4, little);
        if (ifd0 + 2 > view.byteLength) return 1;
        const count = view.getUint16(ifd0, little);
        for (let i = 0; i < count; i++) {
          const entry = ifd0 + 2 + i * 12;
          if (entry + 12 > view.byteLength) return 1;
          if (view.getUint16(entry, little) === 0x0112) {
            const value = view.getUint16(entry + 8, little);
            return value >= 1 && value <= 8 ? value : 1;
          }
        }
        return 1;
      }
    }
    offset += segSize;
  }
  return 1;
}

// Phone JPEGs put EXIF in the first APP1 segment, well within the first 64 KB.
// 128 KB gives comfortable headroom for cameras with thumbnails embedded
// before the orientation tag without paying to read a multi-megabyte file.
const EXIF_SCAN_BYTES = 128 * 1024;

function ensureJpgExtension(name) {
  if (!name) return "image.jpg";
  const dot = name.lastIndexOf(".");
  if (dot < 0) return `${name}.jpg`;
  const ext = name.slice(dot + 1).toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return name;
  return `${name.slice(0, dot)}.jpg`;
}

// `createImageBitmap` with `imageOrientation: "from-image"` is honored by all
// evergreen browsers; older builds silently ignore the option and still
// auto-orient (the spec default flipped to "from-image" years ago), so either
// way we get upright pixels.
async function decodeUpright(file) {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return await createImageBitmap(file);
  }
}

export async function normalizeImageOrientation(file) {
  try {
    if (!file || typeof file !== "object" || !(file instanceof Blob)) {
      return file;
    }
    if (file.__orientationNormalized) return file;

    const type = (file.type || "").toLowerCase();
    if (!JPEG_MIME.has(type)) return file;
    if (typeof createImageBitmap !== "function") return file;

    const headSize = Math.min(file.size, EXIF_SCAN_BYTES);
    const head = await file.slice(0, headSize).arrayBuffer();
    const orientation = readJpegOrientation(head);
    console.log(
      `[EXIF] ${file.name || "(no name)"} | size=${file.size}B | orientation=${orientation} | ${
        orientation === 1 ? "skip (upright)" : "will normalize"
      }`
    );
    if (orientation === 1) return file;

    const bitmap = await decodeUpright(file);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0);
      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
      });
      if (!blob) return file;
      const out = new File(
        [blob],
        ensureJpgExtension(file.name || "image.jpg"),
        {
          type: "image/jpeg",
          lastModified: file.lastModified || Date.now()
        }
      );
      try {
        Object.defineProperty(out, "__orientationNormalized", {
          value: true,
          enumerable: false
        });
      } catch {
        out.__orientationNormalized = true;
      }
      return out;
    } finally {
      if (bitmap && typeof bitmap.close === "function") bitmap.close();
    }
  } catch (err) {
    console.warn(
      "[imageOrientation] normalize failed, uploading original:",
      err && err.message ? err.message : err
    );
    return file;
  }
}

// Rebuilds a FormData with every File/Blob entry passed through
// `normalizeImageOrientation`. Non-file entries (strings, etc.) are copied
// verbatim. Returns the same instance if no entry needed replacing, so we
// don't churn FormData allocations on the 99% no-op path.
export async function normalizeFormDataImages(formData) {
  if (!formData || typeof formData.entries !== "function") return formData;

  const entries = [];
  let changed = false;
  for (const [key, value] of formData.entries()) {
    if (value instanceof Blob) {
      const normalized = await normalizeImageOrientation(value);
      if (normalized !== value) changed = true;
      entries.push([key, normalized, normalized.name]);
    } else {
      entries.push([key, value]);
    }
  }
  if (!changed) return formData;

  const next = new FormData();
  for (const entry of entries) {
    if (entry.length === 3) next.append(entry[0], entry[1], entry[2]);
    else next.append(entry[0], entry[1]);
  }
  return next;
}
